import { useEffect, useState } from "react";
import axios from "axios";
import {
  API, authHeaders, fmt, StatusBadge,
  TH, TD, AdminCard, PageHeading,
  Modal, ModalHeader, inputStyle,
} from "./AdminUtils";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);   // user detail
  const [userOrders, setUserOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailSubject, setEmailSubject]   = useState("");
  const [emailMessage, setEmailMessage]   = useState("");
  const [emailSending, setEmailSending]   = useState(false);
  const [emailResult, setEmailResult]     = useState(null); // { ok, text }

  const load = () => {
    setLoading(true);
    axios.get(`${API}/admin/users`, { headers: authHeaders() })
      .then((r) => setUsers(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openUser = async (u) => {
    setSelected(u);
    setUserOrders([]);
    setOrdersLoading(true);
    setShowEmailForm(false);
    setEmailSubject("");
    setEmailMessage("");
    setEmailResult(null);
    try {
      const res = await axios.get(`${API}/admin/orders/user/${u.id}`, { headers: authHeaders() });
      const sorted = [...res.data].sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate));
      setUserOrders(sorted);
    } catch {
      setUserOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailSubject.trim() || !emailMessage.trim()) {
      setEmailResult({ ok: false, text: "Subject and message are required." });
      return;
    }
    setEmailSending(true);
    setEmailResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/users/${selected.id}/email`,
        { subject: emailSubject, message: emailMessage },
        { headers: authHeaders() }
      );
      setEmailResult({ ok: true, text: res.data.message || "Email sent." });
      setEmailSubject("");
      setEmailMessage("");
      setShowEmailForm(false);
    } catch (e) {
      setEmailResult({ ok: false, text: e.response?.data?.message || "Failed to send email." });
    } finally {
      setEmailSending(false);
    }
  };

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    return (
      !search ||
      u.name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.role?.toLowerCase().includes(q)
    );
  });

  const totalRevenue = (orders) =>
    orders.filter(o => o.paymentStatus === "PAID").reduce((s, o) => s + (o.amount ?? 0), 0);

  return (
    <div>
      <PageHeading title="Users" sub={`${users.length} registered accounts`} />

      {/* Search */}
      <div style={{ marginBottom: 20 }}>
        <input
          placeholder="Search by name, email or role…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...inputStyle, maxWidth: 320 }}
        />
      </div>

      {/* User detail modal */}
      {selected && (
        <Modal onClose={() => setSelected(null)} width={560}>
          <ModalHeader title="User Detail" onClose={() => setSelected(null)} />

          {/* Avatar + basic info */}
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
            <div className="tc-avatar" style={{ width: 52, height: 52, fontSize: 22 }}>
              {selected.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div>
              <div style={{ fontFamily: "var(--font-serif)", fontSize: 20, color: "var(--text-logo)" }}>{selected.name}</div>
              <div style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 2 }}>{selected.email}</div>
              <span style={{
                display: "inline-block",
                marginTop: 6,
                background: selected.role === "ADMIN" ? "var(--warning-bg)" : "var(--bg-secondary)",
                color: selected.role === "ADMIN" ? "var(--warning-text)" : "var(--text-muted)",
                border: `1px solid ${selected.role === "ADMIN" ? "var(--warning-border)" : "var(--border-subtle)"}`,
                padding: "2px 10px",
                borderRadius: "var(--radius-pill)",
                fontSize: 10,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
              }}>
                {selected.role || "USER"}
              </span>
            </div>
          </div>

          {/* Stats row */}
          <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
            {[
              { label: "Total Orders", value: userOrders.length },
              { label: "Paid Orders", value: userOrders.filter(o => o.paymentStatus === "PAID").length },
              { label: "Total Spent", value: fmt(totalRevenue(userOrders)) },
            ].map((s) => (
              <div key={s.label} style={{
                flex: 1,
                background: "var(--bg-secondary)",
                border: "1px solid var(--border-subtle)",
                borderRadius: "var(--radius-sm)",
                padding: "12px 16px",
              }}>
                <div style={{ color: "var(--text-muted)", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 }}>{s.label}</div>
                <div style={{ color: "var(--accent)", fontWeight: 700, fontSize: 18, fontFamily: "var(--font-serif)" }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Send Email */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: showEmailForm ? 12 : 0 }}>
              <div style={{ color: "var(--text-muted)", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.12em" }}>
                Send Email
              </div>
              <button
                onClick={() => { setShowEmailForm(v => !v); setEmailResult(null); }}
                style={{
                  background: showEmailForm ? "var(--bg-secondary)" : "var(--accent-bg)",
                  border: `1px solid ${showEmailForm ? "var(--border-subtle)" : "var(--accent)"}`,
                  color: showEmailForm ? "var(--text-muted)" : "var(--accent)",
                  padding: "4px 14px",
                  borderRadius: "var(--radius-sm)",
                  fontSize: 11,
                  cursor: "pointer",
                  letterSpacing: "0.04em",
                }}
              >
                {showEmailForm ? "Cancel" : "Compose"}
              </button>
            </div>

            {emailResult && (
              <div style={{
                fontSize: 12,
                padding: "9px 12px",
                borderRadius: "var(--radius-sm)",
                marginBottom: 10,
                background: emailResult.ok ? "var(--success-bg, rgba(34,197,94,0.08))" : "var(--danger-bg)",
                border: `1px solid ${emailResult.ok ? "var(--success-border, rgba(34,197,94,0.25))" : "var(--danger-border)"}`,
                color: emailResult.ok ? "var(--success-text, #22c55e)" : "var(--danger-text)",
              }}>
                {emailResult.text}
              </div>
            )}

            {showEmailForm && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div>
                  <div style={{ fontSize: 10, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 5, fontFamily: "var(--font-mono)" }}>
                    Subject
                  </div>
                  <input
                    style={inputStyle}
                    placeholder="e.g. Regarding your recent order"
                    value={emailSubject}
                    onChange={e => setEmailSubject(e.target.value)}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 10, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 5, fontFamily: "var(--font-mono)" }}>
                    Message
                  </div>
                  <textarea
                    style={{ ...inputStyle, minHeight: 100, resize: "vertical", lineHeight: 1.6 }}
                    placeholder="Write your message here..."
                    value={emailMessage}
                    onChange={e => setEmailMessage(e.target.value)}
                  />
                </div>
                <button
                  onClick={handleSendEmail}
                  disabled={emailSending}
                  style={{
                    alignSelf: "flex-end",
                    background: "var(--accent)",
                    border: "none",
                    color: "#000",
                    padding: "8px 20px",
                    borderRadius: "var(--radius-sm)",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: emailSending ? "not-allowed" : "pointer",
                    opacity: emailSending ? 0.7 : 1,
                    letterSpacing: "0.04em",
                  }}
                >
                  {emailSending ? "Sending…" : "Send Email"}
                </button>
              </div>
            )}
          </div>

          {/* Order history */}
          <div style={{ color: "var(--text-muted)", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 12 }}>
            Order History
          </div>

          {ordersLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
              <div className="tc-spinner" />
            </div>
          ) : userOrders.length === 0 ? (
            <div className="tc-empty">
              <div className="tc-empty-dot" />
              No orders yet
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 320, overflowY: "auto" }}>
              {userOrders.map((o) => (
                <div key={o.id} className="tc-order-card">
                  <div>
                    <div className="tc-order-id">Order #{o.id}</div>
                    <div className="tc-order-date">
                      {o.orderDate ? new Date(o.orderDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                    </div>
                    {o.items?.length > 0 && (
                      <div style={{ color: "var(--text-muted)", fontSize: 11, marginTop: 4 }}>
                        {o.items.map(i => `${i.productName} ×${i.quantity}`).join(", ")}
                      </div>
                    )}
                  </div>
                  <div className="tc-order-right">
                    <div className="tc-order-amount">{fmt(o.amount)}</div>
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                      <StatusBadge value={o.paymentStatus} />
                      <StatusBadge value={o.status} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}

      {/* Users table */}
      <AdminCard>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <TH>ID</TH>
              <TH>Name</TH>
              <TH>Email</TH>
              <TH>Role</TH>
              <TH>Orders</TH>
              <TH>Actions</TH>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id}
                style={{ transition: "background var(--transition)" }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--bg-hover)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <TD style={{ color: "var(--text-muted)", width: 50 }}>#{u.id}</TD>
                <TD>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{
                      width: 30, height: 30, borderRadius: "50%",
                      background: "var(--accent-bg)", border: "1px solid var(--accent)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "var(--accent)", fontSize: 13, fontFamily: "var(--font-serif)",
                      flexShrink: 0,
                    }}>
                      {u.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <span style={{ fontWeight: 500, color: "var(--text-primary)" }}>{u.name}</span>
                  </div>
                </TD>
                <TD style={{ color: "var(--text-secondary)", fontSize: 13 }}>{u.email}</TD>
                <TD>
                  <span style={{
                    background: u.role === "ADMIN" ? "var(--warning-bg)" : "var(--bg-secondary)",
                    color: u.role === "ADMIN" ? "var(--warning-text)" : "var(--text-muted)",
                    border: `1px solid ${u.role === "ADMIN" ? "var(--warning-border)" : "var(--border-subtle)"}`,
                    padding: "2px 10px",
                    borderRadius: "var(--radius-pill)",
                    fontSize: 10,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                  }}>
                    {u.role || "USER"}
                  </span>
                </TD>
                <TD style={{ color: "var(--text-secondary)", fontWeight: 500 }}>
                  {u.orderCount ?? "—"}
                </TD>
                <TD>
                  <button
                    onClick={() => openUser(u)}
                    style={{
                      background: "var(--bg-secondary)",
                      border: "1px solid var(--border-default)",
                      color: "var(--text-secondary)",
                      padding: "4px 14px",
                      borderRadius: "var(--radius-sm)",
                      fontSize: 11,
                      letterSpacing: "0.04em",
                    }}
                  >
                    View →
                  </button>
                </TD>
              </tr>
            ))}
          </tbody>
        </table>

        {loading && (
          <div style={{ padding: 40, display: "flex", justifyContent: "center" }}>
            <div className="tc-spinner" />
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="tc-empty" style={{ padding: 48 }}>
            <div className="tc-empty-dot" />
            {search ? "No users match your search" : "No users found"}
          </div>
        )}
      </AdminCard>
    </div>
  );
}